import { useState, useEffect } from 'react';
import { api_editCategories } from '../../../api/category';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { myToastWarning } from '../../../helpers/myToast';
import { getImageUrl } from '../../../api';

interface SubSubSubType {
	name: string;
	description: string;
}

interface SubSubType extends SubSubSubType {
	subSubSubCategories?: SubSubSubType[];
}

interface SubType extends SubSubType {
	subSubCategories?: SubSubType[];
}

const EditCategory = () => {
	const category = useLocation().state;
	const [loading, setLoading] = useState<boolean>(false);
	const [image, setImage] = useState<string | File>(category.image || '');
	const [name, setName] = useState<string>(category.name || '');
	const [des, setDes] = useState<string>(category.description || '');
	const [customSubTypes, setCustomSubTypes] = useState<SubType[]>([]);
	const navigate = useNavigate();

	// Initialize state with existing sub-categories if available
	useEffect(() => {
		if (category.subCategories) {
			const { subCategories } = category;
			setCustomSubTypes(subCategories);
		}
	}, [category]);

	const handleCustomSubTypeChange = (index: number, field: keyof SubType, value: string) => {
		const newCustomSubTypes = [...customSubTypes];
		newCustomSubTypes[index][field] = value as any; // Use type assertion for string fields
		setCustomSubTypes(newCustomSubTypes);
	};

	const handleCustomSubSubTypeChange = (
		subIndex: number,
		subSubIndex: number,
		field: keyof SubSubType,
		value: string,
	) => {
		const newCustomSubTypes = [...customSubTypes];
		if (!newCustomSubTypes[subIndex].subSubCategories) {
			newCustomSubTypes[subIndex].subSubCategories = [];
		}
		newCustomSubTypes[subIndex].subSubCategories![subSubIndex][field] = value as any; // Use type assertion for string fields
		setCustomSubTypes(newCustomSubTypes);
	};

	const handleCustomSubSubSubTypeChange = (
		subIndex: number,
		subSubIndex: number,
		subSubSubIndex: number,
		field: keyof SubSubSubType,
		value: string,
	) => {
		const newCustomSubTypes = [...customSubTypes];
		if (!newCustomSubTypes[subIndex].subSubCategories![subSubIndex].subSubSubCategories) {
			newCustomSubTypes[subIndex].subSubCategories![subSubIndex].subSubSubCategories = [];
		}
		newCustomSubTypes[subIndex].subSubCategories![subSubIndex].subSubSubCategories![
			subSubSubIndex
		][field] = value as any; // Use type assertion for string fields
		setCustomSubTypes(newCustomSubTypes);
	};

	const handleSubCategory = () => {
		setCustomSubTypes([...customSubTypes, { name: '', description: '' }]);
	};

	const handleSubSubCategory = (subIndex: number) => {
		const newCustomSubTypes = [...customSubTypes];
		if (!newCustomSubTypes[subIndex].subSubCategories) {
			newCustomSubTypes[subIndex].subSubCategories = [];
		}
		newCustomSubTypes[subIndex].subSubCategories!.push({ name: '', description: '' });
		setCustomSubTypes(newCustomSubTypes);
	};

	const handleSubSubSubCategory = (subIndex: number, subSubIndex: number) => {
		const newCustomSubTypes = [...customSubTypes];
		if (!newCustomSubTypes[subIndex].subSubCategories![subSubIndex].subSubSubCategories) {
			newCustomSubTypes[subIndex].subSubCategories![subSubIndex].subSubSubCategories = [];
		}
		newCustomSubTypes[subIndex].subSubCategories![subSubIndex].subSubSubCategories!.push({
			name: '',
			description: '',
		});
		setCustomSubTypes(newCustomSubTypes);
	};

	const edit = async () => {
		if (!name) {
			myToastWarning('Please Enter Name');
			return;
		}
		try {
			setLoading(true);
			const payload = new FormData();
			payload.append('name', name);
			payload.append('description', des);
			if (typeof image === 'string' && image) {
				payload.append('image', image);
			} else if (image instanceof File) {
				payload.append('image', image);
			}

			// Append subCategories as FormData
			customSubTypes.forEach((subCategory, index) => {
				payload.append(`subCategories[${index}][name]`, subCategory.name);
				payload.append(`subCategories[${index}][description]`, subCategory.description);

				// Append subSubCategories if present
				if (subCategory.subSubCategories) {
					subCategory.subSubCategories.forEach((subSubCategory, subSubIndex) => {
						payload.append(
							`subCategories[${index}][subSubCategories][${subSubIndex}][name]`,
							subSubCategory.name,
						);
						payload.append(
							`subCategories[${index}][subSubCategories][${subSubIndex}][description]`,
							subSubCategory.description,
						);

						// Append subSubSubCategories if present
						if (subSubCategory.subSubSubCategories) {
							subSubCategory.subSubSubCategories.forEach(
								(subSubSubCategory, subSubSubIndex) => {
									payload.append(
										`subCategories[${index}][subSubCategories][${subSubIndex}][subSubSubCategories][${subSubSubIndex}][name]`,
										subSubSubCategory.name,
									);
									payload.append(
										`subCategories[${index}][subSubCategories][${subSubIndex}][subSubSubCategories][${subSubSubIndex}][description]`,
										subSubSubCategory.description,
									);
								},
							);
						}
					});
				}
			});

			const res = await api_editCategories(category._id, payload);
			const message = typeof res.message === 'string' ? res.message : 'Success';

			if (res.isSuccess) {
				toast.success(message);
				setTimeout(() => {
					navigate(-1);
				}, 100);
			}
		} catch (error) {
			console.error('Error editing category:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				flex: 1,
				padding: '40px',
			}}>
			<div className='mb-3'>
				<h3>Edit Category</h3>
			</div>

			<div className='mb-3'>
				<label htmlFor='exampleFormControlInput1' className='form-label'>
					Name
				</label>
				<input
					value={name}
					onChange={(e) => setName(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					id='exampleFormControlInput1'
					placeholder=''
				/>
			</div>
			<div className='mb-3'>
				<label htmlFor='currentImage' className='form-label'>
					Current Image
				</label>
				{typeof image === 'string' && image && (
					<img
						src={getImageUrl(image)}
						alt='Current Banner'
						style={{ width: '400px', borderRadius: '8px', objectFit: 'cover' }}
						className='form-control form-control-sm'
					/>
				)}
			</div>
			<div className='mb-3'>
				<label htmlFor='formFileSm' className='form-label'>
					Product Image
				</label>
				<input
					onChange={(event) => setImage(event.target.files![0])}
					style={{ width: '400px' }}
					className='form-control form-control-sm'
					id='formFileSm'
					type='file'
				/>
			</div>
			<div className='mb-3'>
				<label htmlFor='exampleFormControlTextarea1' className='form-label'>
					Description
				</label>
				<textarea
					value={des}
					onChange={(e) => setDes(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					id='exampleFormControlTextarea1'
					rows={3}></textarea>
			</div>

			{/* Render custom sub-categories and their controls */}
			{customSubTypes.map((subType, subIndex) => (
				<div key={subIndex} className='mb-3' style={{ width: '400px' }}>
					<input
						value={subType.name}
						onChange={(e) =>
							handleCustomSubTypeChange(subIndex, 'name', e.target.value)
						}
						className='form-control'
						placeholder='Enter sub type name'
					/>
					<input
						value={subType.description}
						onChange={(e) =>
							handleCustomSubTypeChange(subIndex, 'description', e.target.value)
						}
						className='form-control'
						placeholder='Enter sub type description'
						style={{ marginTop: '10px' }}
					/>

					{/* Button to add sub-sub categories */}
					<div className='mb-3'>
						<button
							onClick={() => handleSubSubCategory(subIndex)}
							className='btn btn-secondary'>
							Add Sub Sub Category
						</button>
					</div>

					{/* Render custom sub-sub categories and their controls */}
					{subType.subSubCategories &&
						subType.subSubCategories.map((subSubType, subSubIndex) => (
							<div
								key={subSubIndex}
								className='mb-3'
								style={{ width: '400px', marginLeft: '20px' }}>
								<input
									value={subSubType.name}
									onChange={(e) =>
										handleCustomSubSubTypeChange(
											subIndex,
											subSubIndex,
											'name',
											e.target.value,
										)
									}
									className='form-control'
									placeholder='Enter sub sub type name'
								/>
								<input
									value={subSubType.description}
									onChange={(e) =>
										handleCustomSubSubTypeChange(
											subIndex,
											subSubIndex,
											'description',
											e.target.value,
										)
									}
									className='form-control'
									placeholder='Enter sub sub type description'
									style={{ marginTop: '10px' }}
								/>

								{/* Button to add sub-sub-sub categories */}
								<div className='mb-3'>
									<button
										onClick={() =>
											handleSubSubSubCategory(subIndex, subSubIndex)
										}
										className='btn btn-secondary'>
										Add Sub Sub Sub Category
									</button>
								</div>

								{/* Render custom sub-sub-sub categories and their controls */}
								{subSubType.subSubSubCategories &&
									subSubType.subSubSubCategories.map(
										(subSubSubType, subSubSubIndex) => (
											<div
												key={subSubSubIndex}
												className='mb-3'
												style={{ width: '400px', marginLeft: '40px' }}>
												<input
													value={subSubSubType.name}
													onChange={(e) =>
														handleCustomSubSubSubTypeChange(
															subIndex,
															subSubIndex,
															subSubSubIndex,
															'name',
															e.target.value,
														)
													}
													className='form-control'
													placeholder='Enter sub sub sub type name'
												/>
												<input
													value={subSubSubType.description}
													onChange={(e) =>
														handleCustomSubSubSubTypeChange(
															subIndex,
															subSubIndex,
															subSubSubIndex,
															'description',
															e.target.value,
														)
													}
													className='form-control'
													placeholder='Enter sub sub sub type description'
													style={{ marginTop: '10px' }}
												/>
											</div>
										),
									)}
							</div>
						))}
				</div>
			))}

			{/* Button to add new sub-category */}
			<button
				onClick={handleSubCategory}
				className='btn btn-secondary'
				style={{ marginBottom: '20px' }}>
				Add Sub Category
			</button>

			{/* Button to submit changes */}
			<button
				onClick={edit}
				type='button'
				className='btn btn-lg btn-primary'
				disabled={loading}>
				{loading ? 'Loading..' : 'Submit'}
			</button>
		</div>
	);
};

export default EditCategory;
